// engine.ts — animation clock factory for the trial-beta LP.
//
// Ported from the design attachment's np-engine.js (lines 4136-4291), reduced to
// exactly what the later section modules need: each createClock() call returns its
// own rAF loop, shared by every subscriber *of that instance*, that hands out an
// elapsed-time value each frame. This is NOT one master clock shared across the
// whole page: useSectionAnimation calls createClock() fresh per section mount, so
// a page with several sections visible at once runs that many independent rAF
// loops, each with its own subscriber(s) — never one loop feeding every section.
// The original's full timeline registry (register/reveal/loop/manual kinds, its own
// IntersectionObserver per timeline, ease helpers, enter()/tween() builders) is NOT
// ported here — those concerns belong to individual SectionModule implementations,
// which receive their section's own clock instance and its own
// IntersectionObserver-gated mount (useSectionAnimation) and can build whatever
// timeline shape they need on top of clock.subscribe().
//
// Three deliberate departures from the original, per spec:
//   1. createClock() is a factory, not a singleton hung off `window` — which is
//      exactly why it produces one loop per call rather than one loop per page.
//   2. When the visitor has prefers-reduced-motion, requestAnimationFrame is never
//      called; every subscriber is invoked once, synchronously, with Infinity as the
//      elapsed value so section modules can render their resting/finished frame
//      instead of an empty (t=0) one.
//   3. The loop starts lazily on the first subscribe() and stops for good once the
//      subscriber count reaches zero (rather than running forever from page load).
//
// Page-state class contract (set by Task 3, binding on this engine): np-engine.js
// signalled "engine alive" by adding `np` to <html> before first paint. This page's
// CSS was rescoped to key off `.trial-beta-page.np` instead (150 rules depend on it —
// see docs/trial-system.md and the Task 3/5 cross-task contract), so this port adds
// `np` to the PAGE ROOT (`.trial-beta-page`) rather than `document.documentElement`.
// The add is unconditional and permanent for the page's lifetime — once the engine
// has run, revealed sections must stay revealed, so nothing here ever removes it.
// `np-premium` and `np-p4` are toggled by other scripts outside the ported line range
// (np-engine.js's siblings anim-p4.js and the premium-tier detector) and are out of
// scope for this port; whichever task ports those must scope them the same way.

const PAGE_ROOT_SELECTOR = '.trial-beta-page';
const ENGINE_ALIVE_CLASS = 'np';

function markEngineAlive(): void {
  if (typeof document === 'undefined') return;
  document.querySelector(PAGE_ROOT_SELECTOR)?.classList.add(ENGINE_ALIVE_CLASS);
}

// Exported so useSectionAnimation can decide the `data-anim-running` attribute
// (part of the section-mount contract) with the exact same check createClock()
// uses internally, instead of guessing at the clock's internal running state.
export function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ?? false;
}

export type ClockStep = (elapsedMs: number) => void;

export interface Clock {
  subscribe(step: ClockStep): () => void;
  stop(): void;
}

export function createClock(): Clock {
  markEngineAlive();

  const subscribers = new Set<ClockStep>();
  let rafId: number | null = null;
  let lastWall: number | null = null;
  let elapsed = 0;

  function tick(wall: number): void {
    if (lastWall !== null) elapsed += Math.min(wall - lastWall, 100); // tab-switch guard
    lastWall = wall;
    for (const step of subscribers) step(elapsed);
    rafId = requestAnimationFrame(tick);
  }

  function startLoop(): void {
    if (rafId !== null) return;
    lastWall = null;
    rafId = requestAnimationFrame(tick);
  }

  function stopLoop(): void {
    if (rafId === null) return;
    cancelAnimationFrame(rafId);
    rafId = null;
    lastWall = null;
  }

  function subscribe(step: ClockStep): () => void {
    subscribers.add(step);
    if (prefersReducedMotion()) {
      // No loop is started; render the resting/finished frame once instead of an
      // empty t=0 one so the section shows its settled state, never a blank frame.
      step(Infinity);
    } else {
      startLoop();
    }

    let unsubscribed = false;
    return () => {
      if (unsubscribed) return;
      unsubscribed = true;
      subscribers.delete(step);
      if (subscribers.size === 0) stopLoop();
    };
  }

  function stop(): void {
    subscribers.clear();
    stopLoop();
  }

  return { subscribe, stop };
}
